import { clearSession, currentAccessToken, getRefreshToken, setSession } from '@/auth/session';

/**
 * The ops API client.
 *
 * ── One in-flight refresh, not one per request ─────────────────────────────
 *
 * ⚠ A dashboard page fires several requests at once. If each one notices the
 * expired token and refreshes independently, they race: several refreshes with
 * the same token, and whichever lands last wins while the others' results are
 * discarded. Worse, a server that rotated refresh tokens would invalidate the
 * others mid-flight. One shared promise, awaited by all of them.
 */

const BASE = import.meta.env.VITE_OPS_API_BASE_URL ?? '';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let refreshing: Promise<string | null> | null = null;

async function refreshAccess(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  const res = await fetch(`${BASE}/ops/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!res.ok) {
    // The refresh token is dead — disabled admin, removed passkey, expired.
    // Clearing here is what turns that into a login screen rather than a page
    // that retries forever.
    clearSession();
    return null;
  }
  const data = (await res.json()) as { accessToken: string; expiresIn: number };
  setSession(data.accessToken, data.expiresIn);
  return data.accessToken;
}

async function token(): Promise<string | null> {
  const current = currentAccessToken();
  if (current) return current;
  refreshing ??= refreshAccess().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const access = await token();
  if (!access) throw new ApiError('Your session has ended. Sign in again.', 401);

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(init.headers ?? {}),
      Authorization: `Bearer ${access}`,
    },
  });

  if (res.status === 401) {
    clearSession();
    throw new ApiError('Your session has ended. Sign in again.', 401);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(body.error ?? `Request failed (${res.status})`, res.status);
  }
  return (await res.json()) as T;
}

/** Unauthenticated — the login and registration ceremonies. */
export async function publicApi<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new ApiError(body.error ?? `Request failed (${res.status})`, res.status);
  return body;
}
